import { containsProfanity } from "./badWords";
import { useRoomsStore } from "../store/useRoomsStore";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config"; // adjust based on your setup
import { levelXPTable } from "./levelXPTable";
import { generateCardPack } from "../game/cardPackEngine";
import { useCardStore } from "../store/useCardsStore";
import { infinityCardImageMap } from "./infinityCardImageMap";
import bronzeBadge from "../assets/badges/bronze_badge.png";
import silverBadge from "../assets/badges/silver_badge.png";
import goldBadge from "../assets/badges/gold_badge.png";
import eliteBadge from "../assets/badges/elite_badge.png";
import legendBadge from "../assets/badges/legend_badge.png";
import mythicBadge from "../assets/badges/mythic_badge.png";
import masterBadge from "../assets/badges/master_badge.png";
import infinityMasterBadge from "../assets/badges/infinty_master_badge.png";

// Load the default dictionary once

export function formatNumber(num) {
  console.log("formatNumber called with num:", num);
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return num.toString();
}

export const isValidUsername = (username) => {
  const trimmed = username.trim();
  const usernameRegex = /^[a-zA-Z0-9_]+$/;

  if (!usernameRegex.test(trimmed)) return false;

  const noSpace = trimmed.replace(/\s+/g, "");

  if (containsProfanity(noSpace)) return false;

  if (noSpace.length < 3 || noSpace.length > 20) return false;

  return true;
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim().toLowerCase());
};

export const isValidPassword = (password) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  return passwordRegex.test(password);
};

export const validateAuthInputs = ({ email, username, password }) => {
  //   console.log("email", email, "username", username, "password", password);
  console.log("isValidEmail", isValidEmail(email));
  console.log("isValidPassword", isValidPassword(password));
  console.log("isValidUsername", isValidUsername(username));
  if (!isValidEmail(email))
    return {
      status: false,
      message: "Your Email is not valid. Requirements: Email must be valid.",
    };
  if (!isValidUsername(username))
    return {
      status: false,
      message:
        "Your Username is inappropriate. Requirements: 3-20 characters, no spaces, no special characters, no profanity.",
    };
  if (!isValidPassword(password))
    return {
      status: false,
      message:
        "Your Password is not strong enough. Requirements: At least 8 characters, one uppercase, one lowercase, one number, and one special character.",
    };
  return { status: true, message: "" };
};

export function getColorShades(hexColor) {
  // Helper to convert hex to RGB
  const hexToRgb = (hex) => {
    let parsed = hex.replace(/^#/, "");
    if (parsed.length === 3) {
      parsed = parsed
        .split("")
        .map((c) => c + c)
        .join("");
    }
    const intVal = parseInt(parsed, 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  };

  // Helper to clamp a value between 0 and 255
  const clamp = (val) => Math.max(0, Math.min(255, val));

  // Lighten or darken by a percentage
  const adjust = (color, percent) => {
    const { r, g, b } = color;
    return `#${[
      clamp(Math.round(r + (255 - r) * percent)),
      clamp(Math.round(g + (255 - g) * percent)),
      clamp(Math.round(b + (255 - b) * percent)),
    ]
      .map((c) => c.toString(16).padStart(2, "0"))
      .join("")}`;
  };

  const rgb = hexToRgb(hexColor);
  return {
    light: adjust(rgb, 0.3), // 30% lighter
    main: hexColor,
    dark: adjust(rgb, -0.3), // 30% darker
  };
}

export const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");

  return `${paddedMinutes}:${paddedSeconds}`;
};

export const getToastMessageFromMove = (move) => {
  if (!move || !move.type) return "";

  const {
    attacker,
    type,
    player,
    target,
    defender,
    card,
    damage,
    newStance,
    criticalHit,
  } = move;

  switch (type) {
    case "attack - missed":
      return `⚔️ ${attacker} attacked ${
        target ? target : defender
      } but missed! 👀`;
    case "attack":
      return criticalHit
        ? `💥 ${attacker} CRITICALLY attacked ${
            target ? target : defender
          } for ${damage} damage!`
        : `⚔️ ${attacker} attacked ${
            target ? target : defender
          } for ${damage} damage.`;

    case "playCard":
      return `🧙 ${player} played “${card.name}” on ${target}`;

    case "tradeCard":
      return `🔄 ${player} traded a card with ${target}`;

    case "discardCard":
      return `🗑️ ${player} discarded a card`;

    case "stanceChange":
      return `🛡️ ${player} changed stance to ${newStance}`;

    default:
      return `${player} made a move (${type})`;
  }
};

// Utility to convert level string (e.g., "M1") to numeric index
const getLevelIndex = (levelStr) => {
  const index = levelXPTable.findIndex((lvl) => lvl.level === levelStr);
  return index >= 0 ? index : 0;
};

export const getBadgeByLevel = (level) => {
  const numericLevel = level
    ? parseInt(level.toString().replace("M", "10"))
    : 1;

  if (level?.toString().startsWith("M")) {
    return numericLevel >= 5
      ? { badge: infinityMasterBadge, name: "Master T2" }
      : { badge: masterBadge, name: "Master T1" };
  }

  if (numericLevel >= 85) return { badge: mythicBadge, name: "Mythic" };
  if (numericLevel >= 75) return { badge: legendBadge, name: "Legend" };
  if (numericLevel >= 65) return { badge: eliteBadge, name: "Elite" };
  if (numericLevel >= 50) return { badge: goldBadge, name: "Gold" };
  if (numericLevel >= 25) return { badge: silverBadge, name: "Silver" };
  return { badge: bronzeBadge, name: "Bronze" };
};

export const getLevelFromXP = (xp) => {
  let currentLevel = levelXPTable[0];
  for (const levelObj of levelXPTable) {
    if (xp >= levelObj.cumulativeXP) {
      currentLevel = levelObj;
    } else {
      break;
    }
  }
  return currentLevel;
};

// Random utility
const getRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Generate reward based on level
export const generateLevelUpReward = async (
  userData,
  infinityCards,
  characterCards,
  levelObj
) => {
  try {
    if (!levelObj || !levelObj.reward) {
      console.log("No reward for this level.");
      return;
    }

    console.log("There is a reward for this level");

    const userLevel = levelObj.level;

    let chosenType;

    if (levelObj.issueCardPack) {
      chosenType = "card pack";
    } else {
      const rewardTypes = [
        "finite card",
        "infinity tokens",
        "infinity card",
        // "cosmetic",
        // "avatar",
        // "badge"
      ];
      chosenType = rewardTypes[getRandomInt(0, rewardTypes.length - 1)];
    }

    let rewardData = {
      type: chosenType,
      issuedAt: Date.now(),
      claimed: false,
      reward: null,
      amount: null,
    };

    switch (chosenType) {
      //   case "cosmetic": {
      //     break;
      //   }
      //   case "avatar": {
      //     break;
      //   }
      //   case "badge": {
      //     break;
      //   }
      case "infinty card": {
        if (!infinityCards || infinityCards.length === 0) {
          console.warn("No finite cards available.");
          return;
        }
        const card = [...infinityCards].filter(
          (card) => card.finite === "FALSE" || !card.finite
        )[getRandomInt(0, infinityCards.length - 1)];
        rewardData.reward = card;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: finite card");
        break;
      }
      case "finite card": {
        if (!infinityCards || infinityCards.length === 0) {
          console.warn("No finite cards available.");
          return;
        }
        const card = [...infinityCards].filter(
          (card) => card.finite === "TRUE" || card.finite === true
        )[getRandomInt(0, infinityCards.length - 1)];
        rewardData.reward = card;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: finite card");
        break;
      }

      case "character card": {
        const ownedChars = userData.nftCards || [];
        if (ownedChars.length === 0) {
          console.warn("No character cards owned.");
          return;
        }
        const char = ownedChars[getRandomInt(0, ownedChars.length - 1)];
        rewardData.reward = char;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: character card");
        break;
      }

      case "infinity tokens": {
        const levelNum =
          parseInt(userLevel.replace("M", "")) || parseInt(userLevel);
        rewardData.reward = { currency: "Infinity Tokens" };
        rewardData.amount = getRandomInt(10, Math.min(100, levelNum * 4));
        console.log("Reward: infinity tokens");
        break;
      }

      case "card pack": {
        const pack = generateCardPack(infinityCards, characterCards);
        rewardData.reward = { pack };
        rewardData.amount = 1; // optional field, but good for clarity
        console.log("Reward: card pack");
        break;
      }

      default:
        console.warn("Unknown reward type selected.");
        return;
    }

    // Save to Firestore
    const rewardsRef = collection(db, "users", userData.id, "rewards");
    await addDoc(rewardsRef, rewardData);

    console.log("Reward issued:", rewardData);
    return rewardData;
  } catch (error) {
    console.error("Error generating level-up reward:", error);
  }
};

// Main function to check and update user level
export const checkAndUpdateLevel = async (
  userData,
  newXP,
  infinityCards,
  characterCards
) => {
  try {
    const userRef = doc(db, "users", userData.id);

    const prevLevelObj = getLevelFromXP(userData.xp || 0);
    const newLevelObj = getLevelFromXP(newXP);

    const prevLevelIndex = getLevelIndex(prevLevelObj.level);
    const newLevelIndex = getLevelIndex(newLevelObj.level);

    if (newLevelIndex > prevLevelIndex) {
      // Update user's level
      await updateDoc(userRef, {
        level: newLevelObj.level,
      });

      console.log(
        `🔍 Checking level-up from ${prevLevelObj.level} to ${newLevelObj.level}`
      );

      // Issue rewards for each level gained that has a reward
      for (let i = prevLevelIndex + 1; i <= newLevelIndex; i++) {
        const level = levelXPTable[i];
        if (level.reward) {
          console.log(
            `🎯 Level ${level.level} includes a reward. Card pack: ${level.issueCardPack}`
          );
          await generateLevelUpReward(
            userData,
            infinityCards,
            characterCards,
            level
          );
        }
      }

      console.log(
        `User ${userData.id} leveled up from ${prevLevelObj.level} to ${newLevelObj.level}`
      );
    }

    return newLevelObj;
  } catch (error) {
    console.error("Error in checkAndUpdateLevel:", error);
    return null;
  }
};

export const getQuestProgress = (quest, questLog = []) => {
  let progress = 0;

  questLog.forEach((log) => {
    const stats = log.questStats || {};

    switch (quest.type) {
      case "damage":
        progress += stats.totalDamageDealt || 0;
        break;

      case "playCards":
        progress += stats.totalCardsPlayed || 0;
        break;

      case "cardType":
        progress +=
          stats.typesOfCardsPlayed?.filter((type) => type === quest.subtype)
            .length || 0;
        break;

      case "elementWins":
        if (
          stats.matchWon &&
          stats.characterElement?.toLowerCase() === quest.subtype?.toLowerCase()
        ) {
          progress += 1;
        }
        break;

      case "rarityWins":
        if (
          stats.matchWon &&
          stats.characterRarity?.toLowerCase() === quest.subtype?.toLowerCase()
        ) {
          progress += 1;
        }
        break;

      default:
        break;
    }
  });

  return progress;
};

export const checkAndCompleteQuests = async (userData) => {
  try {
    const { id: userId, quests = [], questLog = [] } = userData;
    if (!userId || quests.length === 0) return [];

    const rewardsRef = collection(db, "users", userId, "rewards");
    const questsRef = collection(db, "users", userId, "quests");

    const batch = writeBatch(db);
    const completedQuests = [];

    for (const quest of quests) {
      if (quest.completed) continue;

      const progress = getQuestProgress(quest, questLog);
      if (progress >= quest.goal) {
        const questDocRef = doc(questsRef, quest.id);

        // ✅ Mark quest as complete
        batch.update(questDocRef, {
          completed: true,
          completedAt: Date.now(),
        });

        // ✅ XP Reward (separate reward)
        const xpReward = {
          type: "quest reward",
          reward: {
            currency: "XP",
          },
          amount: quest.reward.xp,
          claimed: false,
          issuedAt: Date.now(),
          questId: quest.id,
        };

        await addDoc(rewardsRef, xpReward);

        // ✅ Token Reward (separate reward)
        const tokenReward = {
          type: "quest reward",
          reward: {
            currency: "Infinity Tokens",
          },
          amount: quest.reward.tokens,
          claimed: false,
          issuedAt: Date.now(),
          questId: quest.id,
        };

        await addDoc(rewardsRef, tokenReward);

        completedQuests.push({
          quest,
          rewards: [xpReward, tokenReward],
        });

        console.log(`🏁 Quest Completed: ${quest.title}`);
      }
    }

    await batch.commit();

    return completedQuests;
  } catch (err) {
    console.error("❌ Error auto-completing quests:", err);
    return [];
  }
};

// export const updatePlayerPoints = async (roomId, playerId, updates) => {
//   try {
//     const playerPointsRef = doc(
//       db,
//       "rooms",
//       roomId,
//       "matchData",
//       "playerPoints"
//     );
//     const snap = await getDoc(playerPointsRef);
//     if (!snap.exists()) return;

//     const existingData = snap.data();
//     const playerData = existingData[playerId];

//     if (!playerData) return;

//     const updatedPlayerData = {
//       ...playerData,
//       ...Object.entries(updates).reduce((acc, [key, value]) => {
//         acc[key] = Math.min(
//           key === "damageDealtPoints"
//             ? 50
//             : key === "playedCardPoints"
//             ? 50
//             : key === "discardCardPoints" || key === "tradeCardPoints"
//             ? 30
//             : value,
//           (playerData[key] ?? 0) + value
//         );
//         return acc;
//       }, {}),
//     };

//     await updateDoc(playerPointsRef, {
//       [playerId]: updatedPlayerData,
//     });

//     console.log(`✅ Updated points for ${playerId}:`, updatedPlayerData);
//   } catch (err) {
//     console.error("❌ Failed to update player points:", err);
//   }
// };
export const updatePlayerPoints = async (roomId, playerId, updates) => {
  try {
    const playerPointsRef = doc(
      db,
      "rooms",
      roomId,
      "matchData",
      "playerPoints"
    );
    const snap = await getDoc(playerPointsRef);
    if (!snap.exists()) return;

    const existingData = snap.data();
    const playerData = existingData[playerId];
    if (!playerData) return;

    const { questStats = {}, ...pointUpdates } = updates;

    // Handle points accumulation with caps
    const updatedPointData = Object.entries(pointUpdates).reduce(
      (acc, [key, value]) => {
        acc[key] = Math.min(
          key === "damageDealtPoints"
            ? 50
            : key === "playedCardPoints"
            ? 50
            : key === "discardCardPoints" || key === "tradeCardPoints"
            ? 30
            : value,
          (playerData[key] ?? 0) + value
        );
        return acc;
      },
      {}
    );

    // Handle quest stat tracking (raw totals)
    const updatedQuestStats = {
      ...(playerData.questStats || {}),
      ...Object.entries(questStats).reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          acc[key] = [
            ...new Set([...(playerData.questStats?.[key] || []), ...value]),
          ];
        } else {
          acc[key] = (playerData.questStats?.[key] || 0) + value;
        }
        return acc;
      }, {}),
    };

    const updatedPlayerData = {
      ...playerData,
      ...updatedPointData,
      questStats: updatedQuestStats,
    };

    // console.log(
    //   `✅ Updating points and quest stats for ${playerId}:`,
    //   updatedPlayerData
    // );
    await updateDoc(playerPointsRef, {
      [playerId]: updatedPlayerData,
    });

    // console.log(
    //   `✅ Updated points and quest stats for ${playerId}:`,
    //   updatedPlayerData
    // );
  } catch (err) {
    console.error("❌ Failed to update player points:", err);
  }
};

export const addNewLogToQuestLogs = async (userId, questStats) => {
  try {
    if (!userId || !questStats) return;

    const timestamp = Date.now();
    const questLogRef = doc(db, "users", userId, "questLog", `${timestamp}`);

    await setDoc(questLogRef, {
      questStats,
      timestamp,
    });

    console.log(`📘 Quest log saved for ${userId} at ${timestamp}`);
  } catch (err) {
    console.error("❌ Failed to save quest log:", err);
  }
};

export const getPlayerStatsDisplayData = (player, matchData) => {
  if (!player || !matchData) return {};

  const { avatar, character, name, id } = player;

  const moves = matchData?.moves || [];
  const eliminated = matchData?.eliminated || [];

  // 1. Avatar fallback
  const avatarImage = character?.imageUrl;

  // 2. Total moves: this player either played or attacked
  const movesByPlayer = moves.filter(
    (m) => m.player === name || m.attacker === name
  );
  const movesCount = movesByPlayer.length;

  // 3. Eliminations caused by this player
  const eliminationsCount = eliminated.filter(
    (entry) => entry.eliminatedBy?.id === id
  ).length;

  // 4. Total damage dealt by this player
  const damageDealt = moves
    .filter((m) => m.attacker === name && typeof m.damage === "number")
    .reduce((sum, m) => sum + m.damage, 0);

  // 5. Critical hits
  const criticalHits = moves.filter(
    (m) => m.attacker === name && m.criticalHit
  ).length;

  // 6. Cards played
  const cardPlays = moves.filter((m) => m.player === name && m.card).length;

  return {
    avatar: avatarImage,
    movesCount,
    eliminationsCount,
    damageDealt,
    criticalHits,
    cardPlays,
  };
};

export const getCardImageByName = (name, isCharacter = false) => {
  const snakeName = name
    ?.trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s\-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

  let characterCardImageMap; //!UPDATE....

  const imageMap = isCharacter ? characterCardImageMap : infinityCardImageMap;
  const image = imageMap[snakeName];
  //   if (name === "Archer's Charm") {
  //     console.log("Archer's Charm => ", snakeName);
  //     console.log("Archer's Charm image => ", imageMap);
  //   }

  if (!image)
    console.warn(`⚠️ No image found for card: ${name} → ${snakeName}`);

  return image ?? null;
};

// @shared/utils/elementalEffectiveness.js

export const ELEMENTS = [
  "fire",
  "water",
  "rock",
  "grass",
  "air",
  "ice",
  "electric",
  "void",
];

export const ELEMENT_EFFECTIVENESS = {
  fire: { grass: 1, ice: 1, void: 1, water: -1, rock: -1 },
  water: { fire: 1, rock: 1, grass: -1, electric: -1 },
  rock: { electric: 1, ice: 1, grass: -1, water: -1 },
  grass: { water: 1, rock: 1, fire: -1, ice: -1 },
  air: { grass: 1, fire: 1, rock: -1, electric: -1 },
  ice: { grass: 1, air: 1, fire: -1, rock: -1 },
  electric: { water: 1, air: 1, void: 1, rock: -1, grass: -1 },
  void: {
    fire: 1,
    water: 1,
    grass: 1,
    air: 1,
    ice: 1,
    electric: 1,
    void: -1,
  },
};

export function getElementalBonus(attackerElement, defenderElement) {
  const map = ELEMENT_EFFECTIVENESS[attackerElement];
  if (!map) return 0;
  return map[defenderElement] || 0;
}

export function applyBoosts(character) {
  const boosts = character.boosts || {};
  return {
    ...character,
    health: Number(character.health) + (boosts.health || 0),
    attack: Number(character.attack) + (boosts.attack || 0),
    defense: Number(character.defense) + (boosts.defense || 0),
    accuracy: Number(character.accuracy) + (boosts.accuracy || 0),
  };
}

export function getAvailableBoosts(character, selectedBoostsForThisChar = {}) {
  const baseOwned = Number(character.amountOwned || 1);
  const existingBoosts = character.boosts || {};
  const existingBoostTotal = Object.values(existingBoosts).reduce(
    (a, b) => a + b,
    0
  );
  const selectedBoostTotal = Object.values(selectedBoostsForThisChar).reduce(
    (a, b) => a + b,
    0
  );

  // Max 5 total boosts allowed per character
  const remainingBoostsByCap = 5 - (existingBoostTotal + selectedBoostTotal);

  // Max boosts user is allowed to spend is amountOwned - 1 (must keep 1)
  const remainingByOwned = baseOwned - 1 - selectedBoostTotal;

  return Math.max(0, Math.min(remainingBoostsByCap, remainingByOwned));
}

export const specialtyCardEffect = ({
  card,
  actingPlayer,
  targetPlayer,
  players,
  hands,
  decks,
  matchRef,
  roomId,
}) => {
  const updatedPlayers = [...players];
  const actingIndex = updatedPlayers.findIndex((p) => p.id === actingPlayer.id);
  const targetIndex = updatedPlayers.findIndex((p) => p.id === targetPlayer.id);
  let forceEndTurn = false;

  switch (card.name) {
    case "Rivered Return":
      ["health", "defense", "accuracy", "attack"].forEach((stat) => {
        if (targetPlayer.character.buffs[stat]) {
          delete targetPlayer.character.buffs[stat];
        }
      });
      targetPlayer.character.attack = targetPlayer.character.startingAttack;
      targetPlayer.character.defense = targetPlayer.character.startingDefense;
      targetPlayer.character.health = targetPlayer.character.startingHealth;
      targetPlayer.character.accuracy = targetPlayer.character.startingAccuracy;

      //update the updatePlayers array
      updatedPlayers[targetIndex] = targetPlayer;
      break;

    case "Lord's Rune Stone":
      if (actingIndex !== -1 && targetIndex !== -1) {
        // Swap the full player objects in the array
        const temp = updatedPlayers[actingIndex];
        updatedPlayers[actingIndex] = updatedPlayers[targetIndex];
        updatedPlayers[targetIndex] = temp;
        forceEndTurn = true;
      }
      break;

    case "Stoned":
      if (hands?.[targetPlayer.id]?.length) {
        hands[targetPlayer.id] = hands[targetPlayer.id].map((card, index) => {
          return {
            id: `stone-${Date.now()}-${index}`,
            name: "Stone",
            description: "This card is unusable. You must discard it.",
            effect: "none",
            type: "curse",
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/Infinity%20Card%20Images%2FStoned-hand.png?alt=media&token=5badea35-9c11-4dce-adc5-d9aaa9b1faca",
            turnsActive: "Instant",
            areaAffected: [],
            isStone: true,
            bgColor1: "#b5b6ba",
            bgColor2: "#1f2740",
            finite: "TRUE",
          };
        });
      }
      break;

    default:
      break;
  }

  return {
    players: updatedPlayers,
    hands,
    decks,
    forceEndTurn,
  };
};
